import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import { getHalloweenTracking } from "@/lib/halloween-tracking.functions";

export const Route = createFileRoute("/admin_/halloween-tracking")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "Mansion tracking — Recovery Trauma™" },
      { name: "description", content: "Admin view of Halloween email opens, mansion visits and rooms explored." },
      { property: "og:title", content: "Mansion tracking — Recovery Trauma™" },
      { property: "og:description", content: "Admin view of Halloween email opens, mansion visits and rooms explored." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: TrackingPage,
});

const fmt = (s: string) => new Date(s).toLocaleString("en-GB", { timeZone: "Europe/London", dateStyle: "medium", timeStyle: "short" });

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-3xl font-semibold">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function TrackingPage() {
  const fn = useServerFn(getHalloweenTracking);
  const q = useQuery({ queryKey: ["halloween-tracking"], queryFn: () => fn(), refetchInterval: 60_000 });
  const d = q.data;
  const max = Math.max(1, ...(d?.rooms.map((r) => r.members) ?? [1]));

  return (
    <main className="mx-auto max-w-5xl p-4 sm:p-8 space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Admin</Link>
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">Mansion tracking</h1>
        <button onClick={() => q.refetch()} className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>
      {q.isLoading && <p>Loading…</p>}
      {q.error && <p className="text-destructive">Could not load: {(q.error as Error).message}</p>}
      {d && (
        <>
          <p className="text-sm text-muted-foreground">
            {d.email ? <>Since the email “{d.email.subject}” went out on {fmt(d.email.sentAt)}.</> : <>No Halloween email found — showing the last 14 days.</>} Your own admin testing is left out. Private journal words are never shown.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Emails delivered" value={d.email?.delivered ?? 0} />
            <Stat label="Opened the email" value={d.email?.opened ?? 0} hint="Some apps hide opens" />
            <Stat label="Clicked to the mansion" value={d.email?.clicked ?? 0} />
            <Stat label="Mansion door visits" value={d.doorVisits} hint="Browser visits, incl. guests" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Members who played" value={d.players.length} />
            <Stat label="…of them from the email click" value={d.players.filter((p) => p.fromEmail).length} />
          </div>

          <section className="rounded-xl border bg-card p-4">
            <h2 className="font-semibold mb-3">Rooms visited (number of members)</h2>
            <ul className="space-y-1.5">
              {d.rooms.map((r) => (
                <li key={r.room} className="flex items-center gap-2 text-sm">
                  <span className="w-48 shrink-0 truncate">{r.room}. {r.name}</span>
                  <span className="h-3 rounded bg-primary" style={{ width: `${(r.members / max) * 60}%`, minWidth: r.members ? 6 : 0 }} />
                  <span className="text-muted-foreground">{r.members}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border bg-card p-4 overflow-x-auto">
            <h2 className="font-semibold mb-3">Members playing</h2>
            {d.players.length === 0 ? <p className="text-sm text-muted-foreground">No members have played yet.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground"><th className="py-1">Member</th><th>Rooms</th><th>Keys</th><th>From email</th><th>Last seen</th></tr></thead>
                <tbody>
                  {d.players.map((p, i) => (
                    <tr key={i} className="border-t"><td className="py-1.5">{p.name}</td><td>{p.rooms}</td><td>{p.keys}/5</td><td>{p.fromEmail ? "Yes" : "—"}</td><td>{fmt(p.last)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </main>
  );
}
